import { Location } from '@angular/common';
import { Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import moment from 'moment';
import { SeminarDateSeriesDetailsModel } from '../../../../models/seminar.model';
import { SponsorCardModel } from '../../../../models/sponsor-card.model';
import { TrainingCategoryModel } from '../../../../models/training-category.model';
import { FileManagerStateService } from '../../../../services/file-manager-state.service';
import { SponsorCardService } from '../../../../services/sponsor-card.service';
import {
  getSeminarFobiPoints,
  getSeminarOccupancyColor,
  getSeminarUE,
} from '../../../../utils/seminar.utils';
import { getUserFullName } from '../../../../utils/user.utils';

@Component({
  selector: 'app-seminar-details',
  templateUrl: './seminar-details.component.html',
  styleUrls: ['./seminar-details.component.scss'],
  standalone: false,
})
export class SeminarDetailsComponent implements OnInit, OnDestroy {
  @Input() seminarDateSeries: SeminarDateSeriesDetailsModel;
  @Input() isAdmin = false;
  @Input() seminarCategory: TrainingCategoryModel;
  @Input() listView = false;

  constructor(
    private location: Location,
    private router: Router,
    private fileManagerState: FileManagerStateService,
    private sponsorCardService: SponsorCardService,
    private ngZone: NgZone
  ) {}
  public sponsorCards: SponsorCardModel[];
  public sponsorCardsLoading = true;

  public getUserFullName = getUserFullName;
  public getSeminarFobiPoints = getSeminarFobiPoints;
  public getSeminarUE = getSeminarUE;
  public getSeminarOccupancyColor = getSeminarOccupancyColor;

  /** Which meeting credential was last copied (for button feedback). */
  public copiedCredentialKind: 'id' | 'password' | null = null;
  private copyCredentialResetTimer: ReturnType<typeof setTimeout> | null = null;

  async ngOnInit(): Promise<void> {
    this.getSponsorCards();
  }

  private getSponsorCards(): void {
    this.sponsorCardService
      .getSponsorCardsBySeminarId(this.seminarDateSeries.seminar.id)
      .subscribe({
        next: sponsorCards => {
          this.sponsorCards = sponsorCards;
          this.sponsorCardsLoading = false;
        },
        error: error => {
          console.error(error);
          this.sponsorCardsLoading = false;
        },
      });
  }

  public getFreeSeatsText(
    seminarDateSeries: SeminarDateSeriesDetailsModel
  ): string {
    if (
      seminarDateSeries.dates.find(item =>
        moment(item.startDate).isBefore(moment())
      ) &&
      seminarDateSeries.dates.find(item =>
        moment(item.endDate).isAfter(moment())
      )
    ) {
      return 'Läuft';
    }
    const freeSeats = this.freeSeats(seminarDateSeries);
    if (freeSeats <= 0) {
      return 'Warteliste';
    }
    if (freeSeats === 1) {
      return '1 freier Platz';
    }
    return `${freeSeats} freie Plätze`;
  }

  public freeSeats(seminarDateSeries: SeminarDateSeriesDetailsModel): number {
    return (
      seminarDateSeries.seminar.maxParticipants -
      seminarDateSeries.participantCount
    );
  }

  public viewLecturerVita(lecturerId: number): void {
    this.router.navigate(['/lecturer-vita', btoa(lecturerId.toString())]);
  }

  public editSeminar() {
    this.router.navigate([
      'administration/seminars/edit',
      btoa(this.seminarDateSeries.seminar.id.toString()),
    ]);
  }

  public onCancel(): void {
    this.location.back();
  }

  public isOnWaitlist(): boolean {
    return this.seminarDateSeries?.currentUserStatus === 'waitlist';
  }

  public isRegistered(): boolean {
    return this.seminarDateSeries?.currentUserStatus === 'registered';
  }

  public copyMeetingCredential(text: string, kind: 'id' | 'password'): void {
    if (!text?.trim()) {
      return;
    }
    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.ngZone.run(() => {
          if (this.copyCredentialResetTimer) {
            clearTimeout(this.copyCredentialResetTimer);
          }
          this.copiedCredentialKind = kind;
          this.copyCredentialResetTimer = setTimeout(() => {
            this.copiedCredentialKind = null;
            this.copyCredentialResetTimer = null;
          }, 2000);
        });
      })
      .catch(err => {
        console.error('Failed to copy meeting credential', err);
      });
  }

  ngOnDestroy(): void {
    if (this.copyCredentialResetTimer) {
      clearTimeout(this.copyCredentialResetTimer);
    }
    this.fileManagerState.clear();
  }
}
